/**
 * One way of handling a long list, so every screen handles it the same way.
 *
 * The screens grew their own answers: the item-creation modal paginates at
 * fifty, the component picker caps at a 220px box, the constants page does
 * neither and runs to forty rows, and the composite parts table did neither
 * and pushed the rest of the item off the screen once a composite had eleven
 * parts. Three behaviours and two omissions is not a convention.
 *
 * Two pieces, deliberately separable:
 *
 *   ScrollBox   a bounded box. Enough on its own for a list that is long but
 *               finite — a parts table, an audit trail, a room list.
 *   usePaged    pages a list that is long ENOUGH that scrolling it is no
 *               better than not, and pairs with Pager.
 *
 * The counter says "1–50 of 957" rather than "50 shown", because "50 shown"
 * gives a reader no way to tell all of them from the first twentieth of them.
 * With nothing hidden it says the plain count instead, so a short list is not
 * dressed up as a paged one.
 */
import { useState, useEffect, useMemo } from "react";

/** A list in a bounded box. `max` takes any CSS length. */
export function ScrollBox({ max = "38vh", children, style, ...rest }) {
  return (
    <div className="kz-scroll-y"
         style={{ maxHeight: max, overflowY: "auto", ...style }} {...rest}>
      {children}
    </div>
  );
}

/**
 * Page a list.
 *
 * `resetKey` exists because the page number belongs to the list, not to the
 * screen: change which day, item or floor is being shown and page 7 of the old
 * list is not page 7 of the new one — it is usually past the end of it, which
 * reads as an empty screen rather than as a stale page.
 */
export function usePaged(rows, perPage = 25, resetKey = null) {
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [resetKey, perPage]);

  const all = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const pages = Math.max(1, Math.ceil(all.length / perPage));
  const safe = Math.min(page, pages - 1);
  const from = safe * perPage;
  const slice = all.slice(from, from + perPage);

  return {
    slice, page: safe, pages, total: all.length,
    from: all.length ? from + 1 : 0,
    to: Math.min(from + perPage, all.length),
    paged: all.length > perPage,
    setPage,
    next: () => setPage(p => Math.min(p + 1, pages - 1)),
    prev: () => setPage(p => Math.max(p - 1, 0)),
  };
}

const btn = (disabled) => ({
  border: "1px solid #E5E7EB", borderRadius: 6, padding: "3px 10px",
  fontSize: 11.5, background: "#fff",
  color: disabled ? "#C4C9D1" : "#374151",
  cursor: disabled ? "not-allowed" : "pointer",
});

/** The counter and the two controls. Renders nothing when nothing is hidden. */
export function Pager({ p, noun = "row", always = false }) {
  if (!p.paged && !always) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8,
                  fontSize: 11.5, color: "#6B7280" }}>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>
        {p.paged
          ? `${p.from}–${p.to} of ${p.total.toLocaleString("en-IN")}`
          : `${p.total.toLocaleString("en-IN")} ${noun}${p.total === 1 ? "" : "s"}`}
      </span>
      {p.paged && (
        <>
          <div style={{ flex: 1 }} />
          <button onClick={p.prev} disabled={p.page === 0} style={btn(p.page === 0)}>
            Previous</button>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            Page {p.page + 1} of {p.pages}</span>
          <button onClick={p.next} disabled={p.page >= p.pages - 1}
                  style={btn(p.page >= p.pages - 1)}>Next</button>
        </>
      )}
    </div>
  );
}
